<template>
  <v-form
    ref="form"
    v-model="validForm"
    :disabled="isFromActionKit"
    lazy-validation
  >
    <v-card
      max-width="600px"
      class="mx-auto"
      outlined
    >
      <v-card-title :class="$vuetify.breakpoint.mdAndDown ? 'pa-6' : 'pa-10'">
        <v-row class="justify-space-between align-center">
          <v-col>
            <h1 class="headline font-weight-medium">
              <span v-if="!isFromActionKit">{{ isEditing ? `Edit` : `New` }}&nbsp;</span>
              <span class="primary--text">{{ capitalize(editedAction.state) }}&nbsp;</span>
              <span>Action</span>
            </h1>
          </v-col>
          <v-col class="flex-grow-0 pa-0">
            <v-btn
              @click="closeModal"
              v-if="!isEditing"
              text
              fab
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-col>
        </v-row>

        <v-tooltip v-if="isFromActionKit" bottom>
          <template v-slot:activator="{ on }">
            <!-- https://vuetifyjs.com/en/components/buttons#loaders -->
            <v-btn
              v-on="on"
              @click="resyncFromActionKit"
              :loading="syncing"
              :disabled="syncing"
              color="primary"
              icon
              fab
            >
              <v-icon>
                mdi-sync
              </v-icon>
              <template v-slot:loader>
                <span class="custom-loader">
                  <v-icon light>mdi-sync</v-icon>
                </span>
              </template>
            </v-btn>
          </template>
          <span>Re-sync with ActionKit</span>
        </v-tooltip>
      </v-card-title>

      <v-card-text>
        <v-container :class="$vuetify.breakpoint.mdAndDown ? 'pa-0' : ''">
          <v-container v-if="editedAction.createdAt" class="grey--text">
            <p>
              <span v-if="editedAction.createdAt" class="mr-6">
                <strong>Created:</strong> {{ formatTimestamp(editedAction.createdAt) }}
              </span>
              <span v-if="editedAction.updatedAt">
                <strong>Updated:</strong> {{ formatTimestamp(editedAction.updatedAt) }}
              </span>
            </p>
            <p v-if="editedAction.author && user.isAdmin && isEditing" class="mt-2">
              <strong>Author:</strong> {{ editedAction.author.displayName }} (<a
                :href="'mailto:' + editedAction.author.email"
              >{{ editedAction.author.email }}</a>)
            </p>
            <p class="ma-0">
              <span v-if="editedAction.lastCompletedAt" class="mr-6">
                <strong>Last Completed:</strong> {{ formatTimestamp(editedAction.lastCompletedAt) }}
              </span>
              <span v-if="isFromActionKit">
                <strong>ActionKit ID:</strong> {{ editedAction.actionKitId }}
              </span>
            </p>
          </v-container>
          <v-col>
            <v-row>
              <v-col v-if="user.isAdmin">
                <v-switch
                  v-model="editedAction.isUrgent"
                  :disabled="isFromActionKit || editedAction.isFeatured || !userCanEdit"
                  label="Urgent"
                />
              </v-col>
              <v-col>
                <v-switch
                  v-model="editedAction.isFeatured"
                  :disabled="editedAction.isUrgent || !userCanEdit"
                  label="Featured"
                />
              </v-col>
              <v-col v-if="isFromActionKit">
                <v-switch
                  v-model="isStudents"
                  :disabled="isFromActionKit"
                  label="Student"
                  readonly
                />
              </v-col>
            </v-row>
            <v-col v-if="isNationalSelected && editedAction.isUrgent">
              <v-switch
                v-model="editedAction.isFeaturedUrgent"
                :disabled="isFromActionKit"
                label="Mark As Featured Urgent Action"
              />
            </v-col>
          </v-col>

          <StatePickerMultiple
            @updateStates="updateStates"
            :selectedStates="editedAction.states"
            :includeNational="true"
            :disabled="isFromActionKit"
          />

          <v-col cols="12">
            <v-text-field
              v-model="editedAction.headline"
              :disabled="isFromActionKit || !userCanEdit"
              :rules="[rules.required]"
              label="Headline"
              prepend-icon="mdi-format-title"
              class="headline"
              counter="60"
              maxlength="60"
            />

            <v-textarea
              v-model="editedAction.description"
              :disabled="isFromActionKit || !userCanEdit"
              label="Description"
              prepend-icon="mdi-subtitles-outline"
              auto-grow
            />

            <ImageUpload
              v-show="editedAction.isUrgent"
              @setImgFields="setImgFields"
              @setImgUrl="setImgUrl"
              :disabed="!userCanEdit"
              :editedItem="editedAction"
              :imgUrl="editedAction.imgUrl"
              label="Action Image"
            />

            <v-text-field
              v-model="editedAction.cta"
              :disabled="isFromActionKit || !userCanEdit"
              label="Call to Action Button Text"
              prepend-icon="mdi-bullhorn-outline"
              counter="20"
              maxlength="20"
            />

            <v-text-field
              v-model="editedAction.link"
              :disabled="isFromActionKit || !userCanEdit"
              :rules="[rules.url]"
              label="Call to Action Button Link"
              prepend-icon="mdi-link"
              type="url"
            />

            <v-row>
              <v-col cols="5">
                <v-text-field
                  v-model="editedAction.timeToCompleteNum"
                  :disabled="isFromActionKit || !userCanEdit"
                  label="Time to Complete"
                  prepend-icon="mdi-timelapse"
                  type="number"
                />
              </v-col>
              <v-spacer />
              <v-col cols="7">
                <v-autocomplete
                  :items="['minutes', 'hours', 'days']"
                  v-model="editedAction.timeToCompletePeriod"
                  :disabled="isFromActionKit || !userCanEdit"
                  :rules="editedAction.timeToCompleteNum ? [rules.required] : []"
                  label="Time Period"
                  clearable
                />
              </v-col>
            </v-row>

            <template v-if="editedAction.isEvent">
              <v-text-field
                v-model="editedAction.eventLocation"
                :disabled="isFromActionKit"
                label="Event Venue"
                prepend-icon="mdi-home-outline"
              />

              <v-text-field
                v-model="editedAction.eventAddressComplete"
                :disabled="isFromActionKit"
                label="Event Address"
                prepend-icon="mdi-map-marker-outline"
              />

              <v-row>
                <v-col>
                  <v-text-field
                    v-model="eventDate"
                    :disabled="true"
                    label="Event Date"
                    persistent-hint
                    prepend-icon="mdi-calendar-alert"
                    required
                    readonly
                  />
                </v-col>
                <v-col>
                  <v-text-field
                    v-model="eventTime"
                    :disabled="true"
                    label="Event Date"
                    prepend-icon="mdi-calendar-clock"
                    hint="24 hour format"
                    persistent-hint
                    required
                    readonly
                  />
                </v-col>
              </v-row>
            </template>

            <!-- Date picker -->
            <!-- https://vuetifyjs.com/en/components/date-pickers#date-pickers-formatting-date -->
            <v-menu
              ref="datePicker"
              v-else
              v-model="dateMenu"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              max-width="290px"
              min-width="290px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  v-model="eventDate"
                  v-on="on"
                  :disabled="!userCanEdit"
                  :rules="[rules.required]"
                  label="Expiration Date"
                  hint="MM/DD/YYYY format"
                  persistent-hint
                  prepend-icon="mdi-calendar-alert"
                  required
                  readonly
                />
              </template>
              <v-date-picker
                v-model="dashedDate"
                @input="dateMenu = false"
                no-title
              />
            </v-menu>

            <br>

            <v-select
              v-if="user.isAdmin || user.isRegionalApprover"
              v-model="editedAction.workflowState"
              :items="
                user.isAdmin || user.isRegionalApprover
                  ? Object.values(workflowStates)
                  : [workflowStates.draft, workflowStates['pending review']]
              "
              :rules="[rules.required]"
              label="Action Status"
              prepend-icon="mdi-progress-check"
            />

            <RolesInputs
              @updateCategories="updateCategories"
              @updateRoles="updateRoles"
              @updateAffinityGroups="updateAffinityGroups"
              :disabled="!userCanEdit"
              :filterLabel="'Filter action by'"
              :editingCategories="editedAction.categories"
              :editingRoles="editedAction.roles"
              :editingAffinityGroups="editedAction.affinity_groups"
            />

            <v-row>
              <v-col>
                <v-switch
                  v-model="editedAction.volunteerTypeStudent"
                  :disabled="!userCanEdit"
                  label="Show to student volunteers ONLY"
                />
              </v-col>
              <v-col>
                <v-switch
                  v-model="editedAction.volunteerTypeSurvivor"
                  :disabled="!userCanEdit"
                  label="Show to survivor volunteers ONLY"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <v-switch
                  v-model="editedAction.isShowingToTesters"
                  :disabed="!userCanEdit"
                  label="Show only to Test group"
                />
              </v-col>
            </v-row>
          </v-col>

          <br><br>

          <v-card-actions>
            <v-spacer />
            <v-btn
              v-if="isEditing && !isFromActionKit"
              @click="clearChanges"
              text
            >
              Clear Changes
            </v-btn>
            <v-btn
              v-if="!isEditing"
              @click="closeModal"
              color="primary darken-1"
              text
            >
              Cancel
            </v-btn>
            <v-btn
              @click="publish"
              color="primary"
              depressed
            >
              {{
                isEditing
                  ? 'Update'
                  : user.isAdmin || user.isRegionalApprover
                    ? 'Save'
                    : 'Save as Draft'
              }}
            </v-btn>
            <v-btn
              v-if="user.isRegionalAdmin && editedAction.workflowState === 'draft'"
              @click="changeWorkflowState('pending review')"
              color="primary"
              depressed
            >
              Submit for Review
            </v-btn>
            <v-btn
              v-if="
                (user.isAdmin || user.isRegionalApprover) &&
                  isEditing &&
                  editedAction.workflowState === 'pending review'
              "
              @click="changeWorkflowState('draft')"
              color="primary darken-1"
              text
            >
              Return for Edits
            </v-btn>
            <v-btn
              v-if="shouldShowRSVP"
              @click="rsvp"
              color="primary"
              depressed
            >
              RSVP
            </v-btn>
          </v-card-actions>
        </v-container>
      </v-card-text>
    </v-card>
  </v-form>
</template>

<script>
import { mapState } from 'vuex';
import { DateTime } from 'luxon';
import { EventBus, formatISOString } from 'utils';
import { RolesInputs, ImageUpload } from 'components';
import BaseForm from './BaseForm';
import StatePickerMultiple from '../StatePickerMultiple';

export default {
  name: 'ActionForm',

  components: {
    RolesInputs,
    StatePickerMultiple,
    ImageUpload,
  },

  extends: BaseForm,

  props: {
    isEditing: {
      type: Boolean,
      default: false,
    },
    editedAction: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      dateMenu: false,
      isNationalSelected: false,
      workflowStates: {
        draft: {
          text: 'Draft',
          value: 'draft',
        },
        'pending review': {
          text: 'Pending review',
          value: 'pending review',
        },
        published: {
          text: 'Published',
          value: 'published',
        },
        archived: {
          text: 'Archived',
          value: 'archived',
        },
      },
      currentlySelectedStates: [],
    };
  },

  computed: {
    ...mapState(['isDev']),
    ...mapState(['user']),

    shouldShowRSVP() {
      return this.isDev && this.isFromActionKit;
    },

    isFromActionKit() {
      return !!this.editedAction.actionKitId;
    },

    ttCompleteNum() {
      return this.editedAction.timeToCompleteNum;
    },

    ttCompletePeriod() {
      return this.editedAction.timeToCompletePeriod;
    },

    isStudents() {
      return this.editedAction.campaign === 'SDA';
    },

    formattedDate() {
      const { expirationTimestamp } = this.editedAction;
      if (!expirationTimestamp) {
        return null;
      }
      return DateTime.fromISO(expirationTimestamp);
    },

    eventTime() {
      return this.formattedDate.toLocal().toLocaleString(DateTime.TIME_24_SIMPLE);
    },

    eventDate() {
      if (!this.formattedDate) {
        return null;
      }

      return this.formattedDate.toFormat('LL/dd/yyyy');
    },

    dashedDate: {
      get() {
        if (!this.formattedDate) {
          return null;
        }
        return this.formattedDate.toFormat('yyyy-LL-dd');
      },
      set(newValue) {
        this.editedAction.expirationTimestamp = formatISOString(newValue);
      },
    },

    userCanEdit() {
      return !(
        !this.user.isAdmin &&
        !this.user.isRegionalApprover &&
        this.editedAction.workflowState !== 'draft'
      );
    },
  },

  watch: {
    ttCompleteNum() {
      if (!this.ttCompleteNum) {
        this.editedAction.timeToCompletePeriod = '';
      }
    },
    ttCompletePeriod() {
      if (!this.ttCompletePeriod) {
        this.editedAction.timeToCompleteNum = '';
      }
    },
  },

  created() {
    if (this.isEditing) {
      this.isNationalSelected = this.editedAction.states.includes('National');
    }
  },

  updated() {
    this.$nextTick(() => this.serializeAction());
  },

  methods: {
    setImgFields(response) {
      this.editedAction.imgPublicId = response.public_id;
      this.editedAction.imgUrl = response.secure_url;
      this.editedAction.imgFormat = response.format;
      this.editedAction.imgWidth = response.width;
      this.editedAction.imgHeight = response.height;
    },
    setImgUrl(urlToEdit) {
      this.editedAction.imgUrl = urlToEdit;
    },
    updateCategories(categories) {
      this.editedAction.categories = categories;
    },

    updateRoles(roles) {
      this.editedAction.roles = roles;
    },

    updateAffinityGroups(affinity_groups) {
      this.editedAction.affinity_groups = affinity_groups;
    },

    updateStates(states) {
      this.editedAction.states = states;
      this.currentlySelectedStates = states;
      this.isNationalSelected = this.currentlySelectedStates.includes('National');
    },

    async resyncFromActionKit() {
      try {
        this.syncing = true;
        await this.$store.dispatch('syncEvent', this.editedAction);
        EventBus.$emit('alert-success', 'Event synced!');
      } catch (e) {
        EventBus.$emit('alert-error', e);
      } finally {
        this.syncing = false;
      }
    },

    async rsvp() {
      try {
        await this.$store.dispatch('rsvpToAction', this.editedAction);
        EventBus.$emit('alert-success', 'RSVP’d');
        this.closeModal();
      } catch (e) {
        EventBus.$emit('alert-error', e);
      }
    },

    serializeAction() {
      const { timeToCompleteNum, timeToCompletePeriod } = this.editedAction;
      this.editedAction.timeToComplete =
        timeToCompleteNum && timeToCompletePeriod
          ? `${timeToCompleteNum} ${timeToCompletePeriod.toString()}`
          : '';
    },

    publish() {
      if (this.invalidForm) {
        return;
      }
      this.$emit(this.isEditing ? 'update-action' : 'create-action');
    },

    changeWorkflowState(state) {
      if (this.invalidForm) {
        return;
      }
      //Change the workflow state to the value passed
      this.editedAction.workflowState = state;
      this.publish();
    },
  },
};
</script>
